import { useDataCapture } from '@/domains/jobs/hooks/useDataCapture';
import {
  useAddDataCaptureNote,
  useUploadDataCaptureImage,
  useValidateJob,
} from '@/domains/jobs/hooks/useDataCaptureMutations';
import { ImageWithFallback } from '@/shared/components/ImageWithFallback';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import { Camera, Check, CheckCircle2, FileText, Image, Upload, X } from 'lucide-react';
import { useMemo, useState } from 'react';

interface FormResponse {
  question: string;
  answer: string;
}

interface DataCaptureTabProps {
  jobId: string;
  formResponses?: FormResponse[];
  capturedImages?: string[];
  notes?: string[];
  isValidated?: boolean;
  readOnly?: boolean;
}

export function DataCaptureTab({
  jobId,
  formResponses: propFormResponses,
  capturedImages: propCapturedImages,
  notes: propNotes,
  isValidated = false,
  readOnly = false,
}: DataCaptureTabProps) {
  const [newNote, setNewNote] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const numericJobId = Number(jobId);
  const { data: apiData, isLoading } = useDataCapture(numericJobId);
  const addNoteMutation = useAddDataCaptureNote(numericJobId);
  const uploadImageMutation = useUploadDataCaptureImage(numericJobId);
  const validateMutation = useValidateJob(numericJobId);

  // Map API data to component shapes, fallback to props
  const { formResponses, formImages } = useMemo(() => {
    if (propFormResponses && propFormResponses.length > 0)
      return { formResponses: propFormResponses, formImages: [] as string[] };
    if (!apiData?.form_responses)
      return { formResponses: [] as FormResponse[], formImages: [] as string[] };

    const responses: FormResponse[] = [];
    const extractedImages: string[] = [];

    for (const fr of apiData.form_responses) {
      const formData = fr.form_data;

      if (!formData || typeof formData !== 'object') continue;

      // Extract controls from sections - form_data can be:
      // - An array of sections (each with .controls)
      // - A single section object with .controls
      // - A flat object with key/value pairs
      const sections: unknown[] = Array.isArray(formData) ? formData : [formData];

      for (const section of sections) {
        if (!section || typeof section !== 'object') continue;
        const sec = section as Record<string, unknown>;

        // If section has controls array, parse as form controls
        const controls = sec.controls;
        if (Array.isArray(controls)) {
          for (const ctrl of controls) {
            if (!ctrl || typeof ctrl !== 'object') continue;
            const c = ctrl as Record<string, unknown>;
            const controlName = String(c.control_name ?? '');
            const controlType = String(c.control_type ?? '');
            const value = c.value;

            // Skip label-only controls
            if (controlType === 'label') continue;

            // Handle image controls - extract paths
            if (controlType === 's3_image' && Array.isArray(value)) {
              for (const img of value) {
                if (img && typeof img === 'object' && 'path' in (img as Record<string, unknown>)) {
                  extractedImages.push(String((img as Record<string, unknown>).path));
                }
              }
              responses.push({
                question: controlName,
                answer: `${(value as unknown[]).length} photo${(value as unknown[]).length !== 1 ? 's' : ''} captured`,
              });
              continue;
            }

            // Handle map controls
            if (controlType === 'map' && Array.isArray(value)) {
              const coords = value[0];
              if (coords && typeof coords === 'object') {
                const coord = coords as Record<string, unknown>;
                responses.push({
                  question: controlName,
                  answer: `${Number(coord.latitude ?? 0).toFixed(6)}, ${Number(coord.longitude ?? 0).toFixed(6)}`,
                });
              }
              continue;
            }

            // Handle standard controls (radio, number, textarea, conditional_field)
            if (Array.isArray(value)) {
              responses.push({
                question: controlName,
                answer: value
                  .map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)))
                  .join(', '),
              });
            } else if (value != null) {
              responses.push({
                question: controlName,
                answer: String(value),
              });
            }
          }
          continue;
        }

        // Fallback: flat key-value pairs
        if ('question' in sec) {
          responses.push({
            question: String(sec.question ?? ''),
            answer: String(sec.answer ?? ''),
          });
        }
      }
    }
    return { formResponses: responses, formImages: extractedImages };
  }, [propFormResponses, apiData]);

  const capturedImages = useMemo<string[]>(() => {
    if (propCapturedImages && propCapturedImages.length > 0) return propCapturedImages;
    // Combine images from the images table + images extracted from form controls
    const dbImages = (apiData?.captured_images ?? [])
      .map((img) => img.s3_object_key)
      .filter((key): key is string => key != null);
    return [...dbImages, ...formImages];
  }, [propCapturedImages, apiData, formImages]);

  const notes = useMemo<string[]>(() => {
    if (propNotes && propNotes.length > 0) return propNotes;
    if (!apiData?.notes) return [];
    return apiData.notes
      .map((n) => {
        const byLine = n.created_by_name ? ` — ${n.created_by_name}` : '';
        return `${n.note_context ?? ''}${byLine}`;
      })
      .filter((n) => n.trim().length > 0);
  }, [propNotes, apiData]);

  const handleAddNote = async () => {
    const trimmed = newNote.trim();
    if (!trimmed) return;
    try {
      await addNoteMutation.mutateAsync(trimmed);
      showSuccessToast('Note added');
      setNewNote('');
    } catch {
      // Global mutation error handler surfaces the toast; keep the input populated.
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) return;
    try {
      for (const file of selectedFiles) {
        await uploadImageMutation.mutateAsync(file);
      }
      showSuccessToast(
        selectedFiles.length === 1 ? 'Image uploaded' : `${selectedFiles.length} images uploaded`,
      );
      setSelectedFiles([]);
    } catch {
      // Keep selection so the user can retry.
    }
  };

  const handleValidate = async () => {
    try {
      await validateMutation.mutateAsync();
      showSuccessToast('Job validated');
    } catch {
      // Error toast is handled globally.
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E84A2B]" />
        <span className="ml-3 text-muted-foreground text-sm">Loading data capture...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Form Responses Section */}
      {formResponses.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#E84A2B]" />
            <h3 className="font-semibold text-lg">Form Responses</h3>
          </div>
          <div className="space-y-4">
            {formResponses.map((response, index) => {
              // Check if this is the photo request question
              const isPhotoRequest =
                response.question.toLowerCase().includes('picture') ||
                response.question.toLowerCase().includes('photo');

              return (
                <div key={index}>
                  <div className="border-b border-border pb-4 last:border-0 last:pb-0">
                    <p className="font-medium text-foreground mb-2">{response.question}</p>
                    {response.answer && <p className="text-muted-foreground">{response.answer}</p>}
                  </div>

                  {/* Show captured images after the photo request question */}
                  {isPhotoRequest && capturedImages.length > 0 && (
                    <div className="mt-4 mb-4">
                      <div className="grid grid-cols-3 gap-3">
                        {capturedImages.map((imageUrl, imgIndex) => (
                          <div
                            key={imgIndex}
                            className="relative aspect-video rounded-lg overflow-hidden border border-border bg-muted"
                          >
                            <ImageWithFallback
                              src={imageUrl}
                              alt={`Excavation photo ${imgIndex + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {capturedImages.length} photo{capturedImages.length > 1 ? 's' : ''} captured
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Additional Images Upload Section */}
      {!readOnly && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Camera className="w-5 h-5 text-[#E84A2B]" />
            <h3 className="font-semibold text-lg">Additional Images</h3>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Upload higher quality images or additional photos if needed
          </p>

          <div className="border border-dashed border-border rounded-lg p-4">
            <div className="flex flex-col items-center gap-3">
              <Upload className="w-8 h-8 text-muted-foreground" />
              <div className="text-center">
                <p className="text-sm font-medium text-foreground mb-1">Upload Additional Images</p>
                <p className="text-xs text-muted-foreground">
                  Add better quality images or additional photos
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label htmlFor="image-upload">
                  <Button variant="outline" size="sm" asChild>
                    <span className="cursor-pointer">
                      <Image className="w-4 h-4 mr-2" />
                      Choose Files
                    </span>
                  </Button>
                </label>
                {selectedFiles.length > 0 && (
                  <>
                    <span className="text-sm text-muted-foreground">
                      {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                    </span>
                    <Button
                      size="sm"
                      onClick={handleUploadImages}
                      className="text-white"
                      style={{ backgroundColor: '#E84A2B' }}
                      disabled={uploadImageMutation.isPending}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadImageMutation.isPending ? 'Uploading…' : 'Upload'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedFiles([])}
                      disabled={uploadImageMutation.isPending}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-[#E84A2B]" />
          <h3 className="font-semibold text-lg">Quality Check Notes</h3>
        </div>

        {notes.length > 0 && (
          <div className="space-y-3 mb-4">
            {notes.map((note, index) => (
              <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border">
                <p className="text-sm text-foreground">{note}</p>
              </div>
            ))}
          </div>
        )}

        {!readOnly && (
          <div className="space-y-3">
            <Textarea
              placeholder="Add notes about quality, issues, or improvements needed..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="min-h-[100px]"
            />
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={!newNote.trim() || addNoteMutation.isPending}
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
              >
                {addNoteMutation.isPending ? 'Adding…' : 'Add Note'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Validate Button */}
      {!readOnly && !isValidated && (
        <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                Ready to Validate?
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300 mb-4">
                Review all data capture, images, and notes before validating. Once validated, this
                job will be marked as completed and moved to the Completed Jobs tab.
              </p>
              <Button
                size="lg"
                onClick={handleValidate}
                disabled={validateMutation.isPending}
                className="text-white"
                style={{ backgroundColor: '#16a34a' }}
              >
                <Check className="w-5 h-5 mr-2" />
                {validateMutation.isPending ? 'Validating…' : 'Validate Job'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Validated Badge */}
      {isValidated && (
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <div className="flex items-center gap-3">
            <Check className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-100">Job Validated</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                This job has been validated and marked as complete
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
